import React, { useRef, useState } from 'react';
import { ReactSketchCanvas, ReactSketchCanvasRef } from 'react-sketch-canvas';
import { Undo, Redo, DeleteOutline, Create, AutoFixNormal, Check, Close } from '@mui/icons-material';

interface DrawCanvasOverlayProps {
  /** Finish drawing: exports the cropped PNG and stages it into the send bar. */
  onDone: (blob: Blob) => void;
  /** Close the whiteboard without submitting. */
  onCancel: () => void;
}

const COLORS = ['#1e293b', '#16a34a', '#2563eb', '#f59e0b', '#a855f7'];

/**
 * A whiteboard that docks on the RIGHT of the chat (a split view) — the chat
 * stays visible and scrollable on the left while the student draws here. On
 * Submit the strokes are cropped to their bounding box (so the coach gets just
 * the drawing, not the whole board) and staged into the send bar, where a text
 * note can be added before sending. Submit appears only once something is drawn.
 */
const DrawCanvasOverlay: React.FC<DrawCanvasOverlayProps> = ({ onDone, onCancel }) => {
  const canvasRef = useRef<ReactSketchCanvasRef>(null);
  const canvasWrapRef = useRef<HTMLDivElement>(null);
  const [color, setColor] = useState(COLORS[0]);
  const [isEraser, setIsEraser] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [saving, setSaving] = useState(false);

  const refreshHasDrawn = async () => {
    const paths = await canvasRef.current?.exportPaths();
    setHasDrawn(!!paths && paths.length > 0);
  };

  const pickColor = (c: string) => {
    setColor(c);
    setIsEraser(false);
    canvasRef.current?.eraseMode(false);
  };

  const toggleEraser = () => {
    const next = !isEraser;
    setIsEraser(next);
    canvasRef.current?.eraseMode(next);
  };

  // Crop the export to the bounding box of the pen strokes (+ padding) so the
  // coach receives just the drawing, wherever on the board it was drawn.
  const cropToContent = (dataUrl: string, paths: any[]): Promise<Blob> =>
    new Promise((resolve, reject) => {
      let strokes = paths.filter((p) => p?.drawMode);
      if (strokes.length === 0) strokes = paths;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity, maxStroke = 4;
      for (const s of strokes) {
        maxStroke = Math.max(maxStroke, s.strokeWidth || 4);
        for (const pt of s.paths || []) {
          minX = Math.min(minX, pt.x); minY = Math.min(minY, pt.y);
          maxX = Math.max(maxX, pt.x); maxY = Math.max(maxY, pt.y);
        }
      }
      const img = new Image();
      img.onload = () => {
        const elW = canvasWrapRef.current?.clientWidth || img.naturalWidth;
        const elH = canvasWrapRef.current?.clientHeight || img.naturalHeight;
        const sx = img.naturalWidth / elW;
        const sy = img.naturalHeight / elH;
        const pad = maxStroke / 2 + 16;
        const x0 = Math.max(0, minX - pad) * sx;
        const y0 = Math.max(0, minY - pad) * sy;
        const x1 = Math.min(elW, maxX + pad) * sx;
        const y1 = Math.min(elH, maxY + pad) * sy;
        const w = Math.max(1, Math.round(x1 - x0));
        const h = Math.max(1, Math.round(y1 - y0));
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const ctx = c.getContext('2d');
        if (!ctx) return reject(new Error('no 2d context'));
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, x0, y0, x1 - x0, y1 - y0, 0, 0, w, h);
        c.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png');
      };
      img.onerror = reject;
      img.src = dataUrl;
    });

  const handleSubmit = async () => {
    if (!canvasRef.current) return;
    setSaving(true);
    try {
      const paths = await canvasRef.current.exportPaths();
      if (!paths || paths.length === 0) { setSaving(false); return; }
      const dataUrl = await canvasRef.current.exportImage('png');
      const blob = await cropToContent(dataUrl, paths);
      onDone(blob);
    } catch (e) {
      console.error('Failed to export drawing:', e);
    } finally {
      setSaving(false);
    }
  };

  const railBtn = 'flex h-8 w-8 items-center justify-center rounded-full transition';

  return (
    <div className="flex h-full w-[440px] shrink-0 flex-col border-l border-slate-200 bg-white shadow-[-4px_0_16px_rgba(0,0,0,0.04)]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-indigo-700">
          <Create sx={{ fontSize: 18 }} /> Whiteboard
        </div>
        <div className="flex items-center gap-2">
          {hasDrawn && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="inline-flex items-center gap-1 rounded-full bg-indigo-600 px-4 py-1.5 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-60"
            >
              <Check sx={{ fontSize: 16 }} /> {saving ? 'Adding…' : 'Submit'}
            </button>
          )}
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close whiteboard"
            title="Close"
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <Close sx={{ fontSize: 18 }} />
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div ref={canvasWrapRef} className="relative flex-1 bg-white">
        <ReactSketchCanvas
          ref={canvasRef}
          width="100%"
          height="100%"
          strokeWidth={4}
          eraserWidth={18}
          strokeColor={color}
          canvasColor="#ffffff"
          style={{ border: 'none' }}
          onStroke={refreshHasDrawn}
          onChange={refreshHasDrawn}
        />
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-1.5 border-t border-slate-100 px-3 py-2">
        <button type="button" onClick={() => { setIsEraser(false); canvasRef.current?.eraseMode(false); }}
          title="Pen" className={`${railBtn} ${!isEraser ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
          <Create sx={{ fontSize: 17 }} />
        </button>
        <button type="button" onClick={toggleEraser} title="Eraser"
          className={`${railBtn} ${isEraser ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
          <AutoFixNormal sx={{ fontSize: 17 }} />
        </button>
        <button type="button" onClick={() => canvasRef.current?.undo()} title="Undo"
          className={`${railBtn} text-slate-500 hover:bg-slate-100`}>
          <Undo sx={{ fontSize: 17 }} />
        </button>
        <button type="button" onClick={() => canvasRef.current?.redo()} title="Redo"
          className={`${railBtn} text-slate-500 hover:bg-slate-100`}>
          <Redo sx={{ fontSize: 17 }} />
        </button>
        <button type="button" onClick={() => { canvasRef.current?.clearCanvas(); setHasDrawn(false); }} title="Clear"
          className={`${railBtn} text-red-500 hover:bg-red-50`}>
          <DeleteOutline sx={{ fontSize: 17 }} />
        </button>
        <div className="mx-1 h-5 w-px bg-slate-200" />
        {COLORS.map((c) => (
          <button key={c} type="button" onClick={() => pickColor(c)} aria-label={`Pen colour ${c}`}
            className={`h-5 w-5 rounded-full border-2 transition ${
              color === c && !isEraser ? 'border-slate-800 scale-110' : 'border-transparent'
            }`}
            style={{ backgroundColor: c }} />
        ))}
      </div>
    </div>
  );
};

export default DrawCanvasOverlay;
